import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { X, Flame, Snowflake } from 'lucide-react';

const SECTORS = {
  'ai': { etf: 'SOXX', symbols: ['NVDA', 'AMD', 'AVGO', 'INTC', 'QCOM'] },
  'tech': { etf: 'XLK', symbols: ['AAPL', 'MSFT', 'GOOGL', 'META', 'AMZN'] },
  'cloud': { etf: 'WCLD', symbols: ['CRM', 'NOW', 'SNOW', 'DDOG', 'NET'] },
  'crypto': { etf: 'BITO', symbols: ['COIN', 'MSTR', 'MARA', 'RIOT', 'CLSK'] },
  'payments': { etf: 'IPAY', symbols: ['V', 'MA', 'PYPL', 'SQ', 'COIN'] },
  'metals': { etf: 'GLD', symbols: ['GLD', 'SLV', 'GOLD', 'NEM', 'AEM'] },
  'energy': { etf: 'XLE', symbols: ['XOM', 'CVX', 'COP', 'SLB', 'EOG'] },
  'finance': { etf: 'XLF', symbols: ['JPM', 'BAC', 'WFC', 'GS', 'MS'] },
  'healthcare': { etf: 'XLV', symbols: ['UNH', 'JNJ', 'LLY', 'ABBV', 'MRK'] },
  'retail': { etf: 'XLY', symbols: ['AMZN', 'TSLA', 'HD', 'NKE', 'MCD'] },
  'defense': { etf: 'ITA', symbols: ['LMT', 'RTX', 'BA', 'NOC', 'GD'] },
  'ev': { etf: 'ICLN', symbols: ['TSLA', 'RIVN', 'LCID', 'NIO', 'ENPH'] },
  'industrials': { etf: 'XLI', symbols: ['CAT', 'HON', 'UNP', 'GE', 'DE'] },
  'realestate': { etf: 'VNQ', symbols: ['AMT', 'PLD', 'CCI', 'EQIX', 'PSA'] },
  'utilities': { etf: 'XLU', symbols: ['NEE', 'DUK', 'SO', 'D', 'AEP'] }
};

const Gabmap = () => {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSector, setSelectedSector] = useState(null);

  useEffect(() => {
    fetchSectors();
  }, []);

  const fetchSectors = async () => {
    try {
      setLoading(true);
      
      const { data: sectorsData, error: sectorsError } = await supabase
        .from('sectors')
        .select('*')
        .order('sentiment_score', { ascending: false });

      if (sectorsError) throw sectorsError;

      const { data: historyData, error: historyError } = await supabase
        .from('sector_history')
        .select('*')
        .order('date', { ascending: true });

      if (historyError) throw historyError;

      const { data: indicatorsData, error: indicatorsError } = await supabase
        .from('sentiment_indicators')
        .select('*');

      if (indicatorsError) throw indicatorsError;

      const sectorsWithData = sectorsData.map(sector => {
        const sectorHistory = historyData.filter(h => h.sector_id === sector.id);
        const sectorIndicators = indicatorsData.find(i => i.sector_id === sector.id);

        return {
          ...sector,
          sparkline: sectorHistory.map(h => h.sentiment_score),
          socialVolume: sectorHistory.map(h => ({
            volume: h.social_volume,
            sentiment: h.social_sentiment
          })),
          indicators: sectorIndicators || {
            momentum: 50,
            relative_strength: 50,
            options_sentiment: 50,
            social_sentiment: 50,
            money_flow: 50
          }
        };
      });

      setSectors(sectorsWithData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching sectors:', error);
      setLoading(false);
    }
  };

  const getSentimentColor = (sentiment) => {
    if (sentiment >= 75) return '#10b981';
    if (sentiment >= 60) return '#22c55e';
    if (sentiment >= 45) return '#eab308';
    if (sentiment >= 30) return '#fb923c';
    return '#ef4444';
  };

  const getSentimentBg = (sentiment) => {
    if (sentiment >= 75) return 'bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200';
    if (sentiment >= 60) return 'bg-gradient-to-br from-green-50 to-lime-50 border-green-200';
    if (sentiment >= 45) return 'bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200';
    if (sentiment >= 30) return 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-200';
    return 'bg-gradient-to-br from-red-50 to-pink-50 border-red-200';
  };

  const getVelocityIndicator = (velocity) => {
    if (velocity === 'heating') return <Flame className="w-3 h-3 text-orange-500" />;
    if (velocity === 'cooling') return <Snowflake className="w-3 h-3 text-blue-500" />;
    return null;
  };

  const Sparkline = ({ data, color, size = 'normal' }) => {
    if (!data || data.length === 0) return <div className="h-6 bg-gray-100 rounded"></div>;
    
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const width = 120;
    const height = size === 'large' ? 100 : 24;
    
    const points = data.map((val, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((val - min) / range) * height;
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg width="100%" height={height} className="opacity-60" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <polyline points={points} fill="none" stroke={color} strokeWidth={size === 'large' ? '2' : '1.5'} vectorEffect="non-scaling-stroke" />
      </svg>
    );
  };

  const SocialVolumeBars = ({ data, size = 'normal' }) => {
    if (!data || data.length === 0) return <div className="h-6 bg-gray-100 rounded"></div>;
    
    const maxVolume = Math.max(...data.map(d => d.volume));
    const width = 120;
    const height = size === 'large' ? 100 : 24;
    const barWidth = width / data.length;
    
    return (
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        {data.map((d, i) => {
          const barHeight = (d.volume / maxVolume) * height;
          const color = d.sentiment > 0.6 ? '#22c55e' : d.sentiment < 0.4 ? '#ef4444' : '#9ca3af';
          return (
            <rect
              key={i}
              x={i * barWidth}
              y={height - barHeight}
              width={barWidth * 0.8}
              height={barHeight}
              fill={color}
              opacity="0.6"
            />
          );
        })}
      </svg>
    );
  };

  const FearGreedGauge = ({ value, size = 'normal' }) => {
    const radius = size === 'large' ? 50 : 38;
    const centerX = size === 'large' ? 60 : 50;
    const centerY = size === 'large' ? 55 : 45;
    const strokeWidth = size === 'large' ? 6 : 5;
    const needleWidth = size === 'large' ? 2 : 1.5;
    const fontSize = size === 'large' ? 9 : 8;
    const viewWidth = size === 'large' ? 120 : 100;
    const viewHeight = size === 'large' ? 70 : 52;
    
    const rotation = (value / 100) * 180 - 90;
    
    const getGaugeColor = (val) => {
      if (val >= 75) return '#22c55e';
      if (val >= 60) return '#9ca3af';
      if (val >= 40) return '#9ca3af';
      if (val >= 25) return '#9ca3af';
      return '#ef4444';
    };
    
    const getLabelColor = (position) => {
      if (position === 'left' && value < 25) return '#ef4444';
      if (position === 'right' && value >= 75) return '#22c55e';
      return '#9ca3af';
    };
    
    return (
      <div className="relative w-full flex justify-center pb-8">
        <svg width={viewWidth} height={viewHeight} viewBox={`0 0 ${viewWidth} ${viewHeight}`}>
          <path
            d={`M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${centerX + radius} ${centerY}`}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          <path
            d={`M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${centerX + radius} ${centerY}`}
            fill="none"
            stroke={getGaugeColor(value)}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${(value / 100) * Math.PI * radius} ${Math.PI * radius}`}
          />
          <line
            x1={centerX}
            y1={centerY}
            x2={centerX}
            y2={centerY - radius + 4}
            stroke="#4b5563"
            strokeWidth={needleWidth}
            strokeLinecap="round"
            transform={`rotate(${rotation} ${centerX} ${centerY})`}
          />
          <circle cx={centerX} cy={centerY} r="3" fill="#4b5563" />
          <text x={size === 'large' ? 12 : 10} y={size === 'large' ? 55 : 49} fill={getLabelColor('left')} fontSize={fontSize} className="font-mono">FEAR</text>
          <text x={size === 'large' ? 84 : 68} y={size === 'large' ? 55 : 49} fill={getLabelColor('right')} fontSize={fontSize} className="font-mono">GREED</text>
        </svg>
      </div>
    );
  };

  const getOverallSentiment = () => {
    if (sectors.length === 0) return { level: 'NEUTRAL', color: 'text-yellow-600' };
    const avg = sectors.reduce((acc, s) => acc + s.sentiment_score, 0) / sectors.length;
    if (avg >= 60) return { level: 'GREED', color: 'text-green-600' };
    if (avg >= 40) return { level: 'NEUTRAL', color: 'text-yellow-600' };
    return { level: 'FEAR', color: 'text-red-600' };
  };

  const overall = getOverallSentiment();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-green-50 p-8 flex items-center justify-center">
        <div className="text-2xl font-mono text-gray-600">Loading Gabmap...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-green-50 p-8">
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex justify-between items-start mb-8">
          <div className="text-left">
            <h1 className="text-6xl font-light mb-1 bg-gradient-to-r from-red-400 via-yellow-400 via-green-400 to-emerald-500 bg-clip-text text-transparent" style={{fontFamily: 'Georgia, serif'}}>
              Gabmap
            </h1>
            <p className="text-gray-600 font-mono text-sm">
              Map the market's sentiment & themes
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500 mb-1 font-mono">OVERALL MARKET</div>
            <div className={`text-3xl font-bold font-mono ${overall.color}`}>{overall.level}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 sm:gap-6 text-xs mb-6 p-4 bg-white/60 backdrop-blur-sm rounded border border-gray-200/50 font-mono shadow-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gradient-to-br from-red-500 to-red-600 rounded-sm"></div>
            <span className="text-gray-600">FEAR 0-30</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gradient-to-br from-orange-400 to-orange-500 rounded-sm"></div>
            <span className="text-gray-600">CAUTION 30-45</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-sm"></div>
            <span className="text-gray-600">NEUTRAL 45-60</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gradient-to-br from-green-400 to-green-500 rounded-sm"></div>
            <span className="text-gray-600">OPTIMISM 60-75</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-sm"></div>
            <span className="text-gray-600">GREED 75-100</span>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Flame className="w-3 h-3 text-orange-500" />
            <span className="text-gray-600">HEATING</span>
          </div>
          <div className="flex items-center gap-2">
            <Snowflake className="w-3 h-3 text-blue-500" />
            <span className="text-gray-600">COOLING</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {sectors.map((sector) => (
            <div
              key={sector.id}
              className={`${getSentimentBg(sector.sentiment_score)} 
                border-2 rounded cursor-pointer transition-all hover:shadow-lg hover:scale-105
                relative overflow-hidden group p-3 backdrop-blur-sm`}
              onClick={() => setSelectedSector(sector)}
            >
              <div className="flex flex-col h-full">
                <div className="flex items-start justify-between mb-2">
                  <div className="font-mono text-[10px] font-medium text-gray-700 leading-tight pr-1">{sector.name}</div>
                  {getVelocityIndicator(sector.velocity)}
                </div>
                
                <div className="mb-2">
                  <div className="text-4xl font-bold font-mono leading-none" style={{color: getSentimentColor(sector.sentiment_score)}}>
                    {sector.sentiment_score}
                  </div>
                  <div className="text-xs font-mono text-gray-500 mt-0.5">
                    {sector.change_30d > 0 ? '+' : ''}{sector.change_30d}
                  </div>
                </div>

                <div className="mb-3">
                  <FearGreedGauge value={sector.sentiment_score} />
                </div>
                
                <div className="space-y-2 flex-1">
                  <div>
                    <div className="text-[10px] text-gray-400 mb-1 font-mono">30D TREND</div>
                    <Sparkline data={sector.sparkline} color={getSentimentColor(sector.sentiment_score)} />
                  </div>

                  <div>
                    <div className="text-[10px] text-gray-400 font-mono mb-1">SOCIAL VOLUME</div>
                    <SocialVolumeBars data={sector.socialVolume} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedSector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/95 backdrop-blur-md rounded-lg max-w-3xl w-full p-8 relative shadow-2xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setSelectedSector(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>

            <div className="mb-8">
              <h2 className="text-3xl font-light mb-1" style={{fontFamily: 'Georgia, serif'}}>
                {selectedSector.name}
              </h2>
              <p className="text-sm font-mono text-gray-500 mb-1">
                Tracking: {SECTORS[selectedSector.id]?.etf || 'N/A'}
              </p>
              <p className="text-sm font-mono text-gray-500 mb-4">
                Top Holdings: {SECTORS[selectedSector.id]?.symbols.slice(0, 5).join(', ')}
              </p>
              <div className="flex items-baseline gap-3 mb-8">
                <div className="text-5xl font-bold font-mono" style={{color: getSentimentColor(selectedSector.sentiment_score)}}>
                  {selectedSector.sentiment_score}
                </div>
                <div className="text-xl font-mono text-gray-500">
                  {selectedSector.change_30d > 0 ? '+' : ''}{selectedSector.change_30d} pts (30d)
                </div>
              </div>
              <div className="py-4">
                <FearGreedGauge value={selectedSector.sentiment_score} size="large" />
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-mono text-gray-500 mb-3">30-DAY SENTIMENT TREND</h3>
              <div className="h-32 bg-gray-50 rounded border border-gray-200 p-4 flex items-center">
                <Sparkline data={selectedSector.sparkline} color={getSentimentColor(selectedSector.sentiment_score)} size="large" />
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-mono text-gray-500 mb-3">SENTIMENT INDICATORS</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-gray-700">Momentum</span>
                      <div className="group relative">
                        <div className="w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-xs flex items-center justify-center cursor-help">?</div>
                        <div className="absolute left-6 top-0 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                          Price momentum vs 20-day moving average. Higher scores indicate upward price trends.
                        </div>
                      </div>
                    </div>
                    <span className="text-sm font-mono font-bold" style={{color: getSentimentColor(selectedSector.indicators.momentum)}}>
                      {selectedSector.indicators.momentum}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="h-2 rounded-full transition-all" style={{ width: `${selectedSector.indicators.momentum}%`, backgroundColor: getSentimentColor(selectedSector.indicators.momentum) }} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-gray-700">Relative Strength</span>
                      <div className="group relative">
                        <div className="w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-xs flex items-center justify-center cursor-help">?</div>
                        <div className="absolute left-6 top-0 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                          Performance vs market (SPY). Scores above 50 indicate outperformance.
                        </div>
                      </div>
                    </div>
                    <span className="text-sm font-mono font-bold" style={{color: getSentimentColor(selectedSector.indicators.relative_strength)}}>
                      {selectedSector.indicators.relative_strength}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="h-2 rounded-full transition-all" style={{ width: `${selectedSector.indicators.relative_strength}%`, backgroundColor: getSentimentColor(selectedSector.indicators.relative_strength) }} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-gray-700">Options Sentiment</span>
                      <div className="group relative">
                        <div className="w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-xs flex items-center justify-center cursor-help">?</div>
                        <div className="absolute left-6 top-0 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                          Put/Call ratio from options markets. Lower ratios (more calls) indicate bullish institutional positioning.
                        </div>
                      </div>
                    </div>
                    <span className="text-sm font-mono font-bold" style={{color: getSentimentColor(selectedSector.indicators.options_sentiment)}}>
                      {selectedSector.indicators.options_sentiment}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="h-2 rounded-full transition-all" style={{ width: `${selectedSector.indicators.options_sentiment}%`, backgroundColor: getSentimentColor(selectedSector.indicators.options_sentiment) }} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-gray-700">Social Sentiment</span>
                      <div className="group relative">
                        <div className="w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-xs flex items-center justify-center cursor-help">?</div>
                        <div className="absolute left-6 top-0 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                          Retail trader sentiment from social media and forums. Higher scores indicate bullish retail positioning.
                        </div>
                      </div>
                    </div>
                    <span className="text-sm font-mono font-bold" style={{color: getSentimentColor(selectedSector.indicators.social_sentiment)}}>
                      {selectedSector.indicators.social_sentiment}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="h-2 rounded-full transition-all" style={{ width: `${selectedSector.indicators.social_sentiment}%`, backgroundColor: getSentimentColor(selectedSector.indicators.social_sentiment) }} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-gray-700">Money Flow</span>
                      <div className="group relative">
                        <div className="w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-xs flex items-center justify-center cursor-help">?</div>
                        <div className="absolute left-6 top-0 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                          ETF inflows and outflows. Coming soon - placeholder at 50 (neutral).
                        </div>
                      </div>
                    </div>
                    <span className="text-sm font-mono font-bold" style={{color: getSentimentColor(selectedSector.indicators.money_flow)}}>
                      {selectedSector.indicators.money_flow}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="h-2 rounded-full transition-all" style={{ width: `${selectedSector.indicators.money_flow}%`, backgroundColor: getSentimentColor(selectedSector.indicators.money_flow) }} />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-mono text-gray-500 mb-3">30-DAY SOCIAL VOLUME</h3>
              <div className="h-32 bg-gray-50 rounded border border-gray-200 p-4 flex items-end">
                <SocialVolumeBars data={selectedSector.socialVolume} size="large" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gabmap;